import { useEffect, useRef } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import {
  isStreamingCurrentlyActive,
  onStreamingEnd,
  clearPendingCallbacks,
} from "@/lib/streamingState";

/**
 * PWA auto-update hook
 * Checks for updates every 60 seconds and automatically applies them
 *
 * STREAMING-AWARE: Defers updates when baby is streaming or parent is consuming stream.
 * The update will be applied automatically when streaming ends.
 */
export function usePWA() {
  // Track if we have a pending update
  const pendingUpdateRef = useRef<(() => void) | null>(null);

  const { updateServiceWorker } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        // Check for updates every 60 seconds
        setInterval(() => {
          registration.update();
        }, 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error("SW registration failed:", error);
    },
    onOfflineReady() {
      console.log("App is ready to work offline");
    },
    onNeedRefresh() {
      console.log("[PWA] New content available, checking streaming state...");

      const applyUpdate = () => {
        console.log("[PWA] Applying update and reloading...");
        // updateServiceWorker(true) will skip waiting and reload
        updateServiceWorker(true);
      };

      // Check if streaming is active
      if (isStreamingCurrentlyActive()) {
        console.log(
          "[PWA] Streaming active - deferring update until streaming ends",
        );
        pendingUpdateRef.current = applyUpdate;
        // Register callback for when streaming ends
        onStreamingEnd(applyUpdate);
      } else {
        // No streaming, apply update immediately
        applyUpdate();
      }
    },
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending callbacks if component unmounts
      clearPendingCallbacks();
      pendingUpdateRef.current = null;
    };
  }, []);
}
